import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Users,
  Tag,
  Truck,
  Settings,
  FileText,
  Mail,
  LogOut,
  Menu,
  X,
  ChevronRight,
  CreditCard,
  Bell,
  MessageCircle,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { adminAPI } from '../../api/admin';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchNotifications = async () => {
    try {
      const res = await adminAPI.getNotifications();
      if (res.success && res.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await adminAPI.markAllNotificationsRead();
      fetchNotifications();
    } catch {}
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

const role = user?.role;
const userPermissions: string[] = (user as any)?.permissions || [];
const hasFullAccess = role === 'admin' || role === 'super_admin';
const canAccess = (permission?: string) => {
  if (!permission) return true;
  if (hasFullAccess) return true;
  return userPermissions.includes(permission);
};

const baseNavItems = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard, permission: undefined as string | undefined },
  { label: 'Products', href: '/admin/products', icon: Package, permission: 'products' },
  { label: 'Orders', href: '/admin/orders', icon: ShoppingBag, permission: 'orders' },
  ...(hasFullAccess ? [{ label: 'Customers', href: '/admin/customers', icon: Users, permission: undefined as string | undefined }] : []),
  { label: 'Categories', href: '/admin/categories', icon: Tag, permission: 'categories' },
  { label: 'Inventory', href: '/admin/inventory', icon: Package, permission: 'inventory' },
  { label: 'Discounts', href: '/admin/discounts', icon: Tag, permission: 'discounts' },
  { label: 'Shipping', href: '/admin/shipping', icon: Truck, permission: 'shipping' },
  { label: 'Reviews', href: '/admin/reviews', icon: FileText, permission: 'reviews' },
  { label: 'Newsletter', href: '/admin/newsletter', icon: Mail, permission: 'newsletter' },
  { label: 'Messages', href: '/admin/messages', icon: Mail, permission: 'contact' },
  { label: 'Support Chat', href: '/admin/support', icon: MessageCircle, permission: 'support' },
  { label: 'Payments', href: '/admin/payments', icon: CreditCard, permission: 'payments' },
];

const navItems = [
  ...baseNavItems.filter((item) => canAccess(item.permission)),
  ...(isSuperAdmin
    ? [
        { label: 'Staff', href: '/admin/staff', icon: Users },
        { label: 'Settings', href: '/admin/settings', icon: Settings },
        { label: 'Activity Logs', href: '/admin/logs', icon: FileText },
      ]
    : []),
];

  return (
    <div className="min-h-screen bg-light flex">
      {/* Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 240, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-primary text-white flex flex-col overflow-hidden flex-shrink-0"
          >
            {/* Logo */}
            <div className="px-6 py-6 border-b border-secondary">
              <Link to="/" className="font-serif text-lg tracking-widest uppercase">
                Lanre Savage
              </Link>
              <p className="text-xs text-nude-light mt-1 tracking-widest uppercase">
                Admin Panel
              </p>
            </div>

            {/* Nav */}
            <nav className="flex-1 py-6 overflow-y-auto">
              {navItems.map(({ label, href, icon: Icon }) => {
                const isActive = location.pathname === href;
                return (
                  <Link
                    key={href}
                    to={href}
                    className={`flex items-center justify-between px-6 py-3 text-xs tracking-widest uppercase transition-colors ${
                      isActive
                        ? 'bg-secondary text-white'
                        : 'text-nude-light hover:text-white hover:bg-secondary'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon size={14} />
                      <span>{label}</span>
                    </div>
                    {isActive && <ChevronRight size={12} />}
                  </Link>
                );
              })}
            </nav>

            {/* User */}
            <div className="px-6 py-4 border-t border-secondary">
              <p className="text-xs text-nude-light tracking-widest uppercase mb-1">
                {user?.role}
              </p>
              <p className="text-xs text-white">
                {user?.first_name} {user?.last_name}
              </p>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-xs text-nude-light hover:text-white transition-colors mt-3"
              >
                <LogOut size={12} />
                <span className="tracking-widest uppercase">Sign Out</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-light px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-primary hover:text-nude transition-colors"
          >
            {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors"
            >
              View Store
            </Link>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="text-primary hover:text-nude transition-colors relative"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-light shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-light">
                    <span className="text-xs tracking-widest uppercase text-primary">
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-nude hover:text-primary transition-colors"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-xs text-nude tracking-widest uppercase">
                      No notifications
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`p-4 border-b border-light text-xs ${
                          !n.is_read ? 'bg-light/50' : ''
                        }`}
                      >
                        <p className="font-medium text-primary mb-1">{n.title}</p>
                        <p className="text-secondary leading-relaxed">{n.message}</p>
                        <p className="text-nude mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="w-8 h-8 bg-primary text-white flex items-center justify-center text-xs font-medium">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;